import React from 'react';
import ReactPlayer from 'react-player/file';

import { filterLiveLogs } from '~/src/dash/api/live_logs/helpers';
import { ILiveStreamWatcherAdBreakData } from '~/src/dash/api/live_logs/models';
import { LiveLogsGeneralTable } from '~/src/dash/components/live_logs/live-logs-general-table';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { Cols, Col, Row, Rows, Span, Text } from '~/src/dash/publicaUI';
import { Chip, Icon, IconButton } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { GarmScoreChips } from '~/src/dash/components/GarmScoreChips';

export interface IProps {
  data: ILiveStreamWatcherAdBreakData[];
  siteID?: number;
}

export const LiveContextAdBreakTable = ({ data, siteID }: IProps): React.ReactElement => {
  const { session } = useBaseStore();

  const renderCopyIcon = (data: string): React.ReactElement => {
    return (
      <IconButton onClick={() => copy(data)} size='small'>
        <Icon style={{ fontSize: '0.9em' }} color='disabled'>
          content_copy
        </Icon>
      </IconButton>
    );
  };

  const renderGarmScores = (row: ILiveStreamWatcherAdBreakData): React.ReactElement => {
    return (
      <Rows>
        <Row noPadding marginTop='0.5em' marginBottom='0.5em'>
          <Cols>
            <Col>
              <Text bold small>
                Brand Safety
              </Text>
            </Col>
            <Col>{renderCopyIcon(row.garmScores)}</Col>
          </Cols>
        </Row>
        <GarmScoreChips row={row} publisherID={session.publisher.ID} siteID={siteID || 0} />
      </Rows>
    );
  };

  const renderGarmScoresDirect = (row: ILiveStreamWatcherAdBreakData): React.ReactElement => {
    return (
      <Rows>
        <Row noPadding>
          <Cols>
            <Col>
              <Text bold small>
                Garm Scores Direct
              </Text>
            </Col>
            <Col>{renderCopyIcon(row.garmScoresDirect)}</Col>
          </Cols>
        </Row>
        <GarmScoreChips
          row={row}
          publisherID={session.publisher.ID}
          siteID={siteID || 0}
          isDirect={true}
        />
      </Rows>
    );
  };

  const renderPersonality = (personality: string): React.ReactElement => {
    const personalityArray = personality.split(',');
    return (
      <Rows>
        <Row noPadding marginTop='0.5em' marginBottom='0.5em'>
          <Cols>
            <Col>
              <Text bold small>
                Personality
              </Text>
            </Col>
            <Col>{renderCopyIcon(personality)}</Col>
          </Cols>
        </Row>
        <Row noPadding marginLeft='1em' displayFlex>
          {personalityArray.map((item) => (
            <Chip
              style={{
                margin: '0 0 0.3em 0.3em',
              }}
              size='small'
              label={item}
            />
          ))}
        </Row>
      </Rows>
    );
  };

  return (
    <LiveLogsGeneralTable<ILiveStreamWatcherAdBreakData>
      publisherTimezone={session.publisher.Timezone}
      data={filterLiveLogs(data, '')}
      columns={[
        {
          title: 'Timestamp',
          field: 'timestamp',
          searchable: false,
          width: '40px',
          cellStyle: { whiteSpace: 'pre-wrap' },
        },
        {
          title: 'Video URL',
          field: 'url',
          searchable: false,
          cellStyle: { maxWidth: 1200 },
        },
      ]}
      nestedData={[
        {
          title: 'Debug details',
          isNested: true,
          getData: (row: ILiveStreamWatcherAdBreakData) => row,
          preFormat: 'text',
          render: (row: ILiveStreamWatcherAdBreakData): React.ReactElement => {
            return (
              <Rows>
                <Row noPadding>
                  <Text small>
                    <Span bold>Context Delay: </Span>
                    <Span>{row.contextDelay}s</Span>
                  </Text>
                </Row>
                <Row noPadding>
                  <Text small>
                    <Span bold>Publica Delay: </Span>
                    <Span>{row.publicaDelay}s</Span>
                  </Text>
                </Row>
                <Row noPadding>
                  <Text small>
                    <Span bold>Semantic IDs: </Span>
                    <Span>{row.semanticIDs ? row.semanticIDs.split(',').join(', ') : 'none'}</Span>
                  </Text>
                </Row>
                <Row noPadding>
                  <Text small>
                    <Span bold>Semantic IDs Direct: </Span>
                    <Span>
                      {row.semanticIDsDirect ? row.semanticIDsDirect.split(',').join(', ') : 'none'}
                    </Span>
                  </Text>
                </Row>
                {row.garmScoresDirect && <Row noPadding>{renderGarmScoresDirect(row)}</Row>}
              </Rows>
            );
          },
        },
        {
          title: 'Content Signals',
          getData: (row: ILiveStreamWatcherAdBreakData) => row,
          preFormat: 'text',
          render: (row: ILiveStreamWatcherAdBreakData): React.ReactElement => {
            return (
              <Rows>
                {row.garmScores && <Row noPadding>{renderGarmScores(row)}</Row>}
                {row.personality && <Row noPadding>{renderPersonality(row.personality)}</Row>}
              </Rows>
            );
          },
        },
        {
          title: 'Video Clip',
          getData: (row: ILiveStreamWatcherAdBreakData) => row.url,
          getDataToCopy: (row: ILiveStreamWatcherAdBreakData) => row.url,
          render: (liveContextURL: string): React.ReactElement => {
            return (
              <ReactPlayer
                style={{
                  backgroundColor: 'black',
                  borderRadius: '1em',
                  overflow: 'hidden',
                }}
                className='react-player fixed-bottom'
                url={liveContextURL}
                width='100%'
                height='300px'
                controls={true}
              />
            );
          },
        },
      ]}
    />
  );
};
